import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";
import { getToken } from "./auth";

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

// Live updates over Server-Sent Events, read with fetch() so the bearer token goes in a header (not the URL).
// Reconnects with backoff. `onChange` is debounced (1 s). Returns true while the stream is connected.
export function useLive(onChange) {
  const cb = useRef(onChange);
  cb.current = onChange;
  const [live, setLive] = useState(false);
  useEffect(() => {
    let stop = false;
    let ctrl;
    let timer;
    const fire = () => { clearTimeout(timer); timer = setTimeout(() => cb.current && cb.current(), 1000); };
    (async () => {
      let delay = 2000;
      while (!stop) {
        try {
          ctrl = new AbortController();
          const r = await fetch(`${api.defaults.baseURL}/estate/stream`, { headers: { Authorization: `Bearer ${getToken()}` }, signal: ctrl.signal });
          if (r.status === 403) { stop = true; setLive(false); return; } // access ended (e.g. support session revoked): stop retrying
          if (!r.ok || !r.body) throw new Error(`HTTP ${r.status}`);
          setLive(true); delay = 2000;
          const rd = r.body.getReader(); const dec = new TextDecoder(); let buf = "";
          for (;;) {
            const { value, done } = await rd.read();
            if (done) break;
            buf += dec.decode(value, { stream: true });
            let i;
            while ((i = buf.indexOf("\n\n")) >= 0) {
              const ev = buf.slice(0, i); buf = buf.slice(i + 2);
              if (ev.includes("event: change")) fire();
              if (ev.includes("event: revoked")) { stop = true; ctrl.abort(); } // support session ended: do not reconnect
            }
          }
        } catch { /* fall through to reconnect */ }
        if (stop) return;
        setLive(false);
        await sleep(delay);
        delay = Math.min(delay * 2, 30000);
      }
    })();
    return () => { stop = true; clearTimeout(timer); if (ctrl) ctrl.abort(); };
  }, []);
  return live;
}

// Runs `load` on mount, whenever the server reports a change, and every 30 s while the stream is down.
export function useLiveData(load) {
  const ref = useRef(load);
  ref.current = load;
  const run = useCallback(() => ref.current(), []);
  useEffect(() => { run(); }, [run]);
  const live = useLive(run);
  useEffect(() => {
    if (live) return undefined;
    const t = setInterval(run, 30000);
    return () => clearInterval(t);
  }, [live, run]);
  return live;
}
